import {useEffect} from 'react';
import {useEditor, EditorContent} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';

import {EditorProps} from './types';
import {StyledEditor} from './styles';
import Toolbar from './toolbar';

// ----------------------------------------------------------------------

export default function Editor({
  id = 'tiptap-editor',
  error,
  simple = false,
  helperText,
  sx,
  toolbarpresent,
  imagesVideoOpen,
  editorRef,
  value,
  onChange,
  placeholder = 'Write something awesome...',
  ...other
}: EditorProps) {
  const isEditable =
    toolbarpresent === undefined || toolbarpresent === true;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: {
          HTMLAttributes: {
            style:
              'border-left: 3px solid #ccc; padding: 8px 16px; margin: 8px 0; background-color: #f9f9f9;'
          }
        }
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Link.configure({
        openOnClick: false
      }),
      Image,
      Highlight,
      TextStyle
    ],
    content: value || '',
    editable: isEditable,
    onUpdate: ({editor: ed}) => {
      onChange?.(ed.getHTML());
    }
  });

  // Sync external value changes (e.g. loading from API)
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentHTML = editor.getHTML();
      // Only update if the value actually changed from outside
      if (currentHTML !== value && !(value === '' && currentHTML === '<p></p>')) {
        editor.commands.setContent(value, false);
      }
    }
  }, [value, editor]);

  // Expose editor via ref if provided
  useEffect(() => {
    if (editorRef && editor) {
      editorRef.current = editor;
    }
  }, [editorRef, editor]);

  if (!editor) return null;

  const showToolbar = toolbarpresent === undefined || toolbarpresent === true;

  return (
    <div dir="ltr" style={{direction: 'ltr', textAlign: 'left'}}>
      <StyledEditor
        error={error}
        sx={sx}>
        {showToolbar && (
          <Toolbar
            editor={editor}
            simple={simple}
            imagesVideoOpen={imagesVideoOpen}
          />
        )}

        <EditorContent
          editor={editor}
          className="tiptap-editor-content"
        />
      </StyledEditor>

      {helperText && helperText}
    </div>
  );
}
